<template>
  <el-row :gutter="spacingWidthHeaderComponents" class="mb-2">
    <div class="button-row">
      <el-button
        v-if="runnableApiNextRuns.length"
        type="primary"
        @click="showRunMultipleModal = true"
      >
        <h4>{{ $t('runMultipleApiImports') }}</h4>
      </el-button>
      <el-button
        v-if="apiConnectionConfigurations.length"
        type="info"
        class="configure-button"
        @click="navigateToConfiguration"
      >
        <h4>{{ $t('configureApiImports') }}</h4>
        <el-icon class="el-icon--right">
          <ArrowRight />
        </el-icon>
      </el-button>
    </div>
  </el-row>
  <el-row :gutter="spacingWidthHeaderComponents">
    <base-table
      :show-loading-table="showLoadingTable"
      :show-no-content-table="!apiNextRuns.length"
      :error-message="$t('noApiNextRunsToDisplay')"
    >
      <el-table v-if="apiNextRuns.length" :data="apiNextRuns">
        <el-table-column :label="$t('outputFileName')" min-width="200">
          <template #default="scope">
            <h4>{{ scope.row.outputFileName }}</h4>
          </template>
        </el-table-column>
        <el-table-column :label="$t('apiConnectionName')" min-width="200">
          <template #default="scope">
            <div class="d-flex align-items-center">
              <h4>{{ getApiNameKey(scope.row.apiName) }}</h4>
              <el-tooltip
                v-if="isApiConnectionDisabled(scope.row.apiName)"
                placement="top"
                :content="$t('apiConnectionDisabledWarning')"
              >
                <el-icon class="ml-2 color-red">
                  <WarningFilled />
                </el-icon>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column :label="$t('nextRun')" min-width="180">
          <template #default="scope">
            <h4>{{ formatTimestamp(scope.row.nextRunTimestamp) }}</h4>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('scheduleCalculationResult')"
          min-width="180"
        >
          <template #default="scope">
            <el-tooltip
              v-if="
                scope.row.statusMessage &&
                scope.row.nextRunCalculationResult !== 0
              "
              placement="top"
              :content="scope.row.statusMessage"
            >
              <div class="d-flex align-items-center status-with-tooltip">
                <el-icon
                  :class="
                    getStatusInfo(scope.row.nextRunCalculationResult).colorClass
                  "
                  class="mr-1"
                >
                  <component
                    :is="getStatusInfo(scope.row.nextRunCalculationResult).icon"
                  />
                </el-icon>
                <span>
                  {{ getStatusInfo(scope.row.nextRunCalculationResult).text }}
                </span>
              </div>
            </el-tooltip>
            <div v-else class="d-flex align-items-center">
              <el-icon
                :class="
                  getStatusInfo(scope.row.nextRunCalculationResult).colorClass
                "
                class="mr-1"
              >
                <component
                  :is="getStatusInfo(scope.row.nextRunCalculationResult).icon"
                />
              </el-icon>
              <span>
                {{ getStatusInfo(scope.row.nextRunCalculationResult).text }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column min-width="120">
          <template #default="scope">
            <div class="d-flex">
              <el-button
                v-if="canRunImport(scope.row)"
                type="primary"
                size="small"
                class="ml-auto"
                @click="
                  runApiImports([
                    {
                      apiName: scope.row.apiName,
                      outputFileName: scope.row.outputFileName
                    }
                  ])
                "
              >
                {{ $t('runNow') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </base-table>
  </el-row>
  <api-import-selection-modal
    :dialog-visible="showRunMultipleModal"
    :api-next-runs="runnableApiNextRuns"
    :modal-title="$t('selectApiImportsToRun')"
    @confirm="handleRunMultipleConfirm"
    @close="showRunMultipleModal = false"
  />
</template>
<script lang="ts">
import {
  ElButton,
  ElMessage,
  ElRow,
  ElTable,
  ElTableColumn,
  ElTooltip,
  ElIcon
} from 'element-plus';
import {
  WarningFilled,
  CircleCheckFilled,
  CircleCloseFilled,
  InfoFilled,
  ArrowRight
} from '@element-plus/icons-vue';
import { onMounted, computed, ComputedRef, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { spacingWidthHeaderComponents } from '@/utils/variables';
import BaseTable from '@/components/tables/BaseTable.vue';
import {
  ApiConnectionConfiguration,
  ApiName,
  ApiNextRunConfiguration,
  NextRunCalculationResult,
  StartApiRunRequest,
  StartApiRunSet
} from '@etp/etp-gain-bff-client/axios';
import moment from 'moment';
import ApiImportSelectionModal from './components/ApiImportSelectionModal.vue';
import { ApiImportSelection, RunApiImport } from './models/ApiImportSelection';
import i18n from '@/i18n';

export default {
  name: 'GainApiNextRuns',
  components: {
    BaseTable,
    ElButton,
    ElRow,
    ElTable,
    ElTableColumn,
    ElTooltip,
    ElIcon,
    WarningFilled,
    CircleCheckFilled,
    CircleCloseFilled,
    InfoFilled,
    ArrowRight,
    ApiImportSelectionModal
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const showLoadingTable = ref(true);
    const showRunMultipleModal = ref(false);

    const apiNextRuns: ComputedRef<Array<ApiNextRunConfiguration>> = computed(
      () => store.getters['gain/getApiNextRuns']
    );

    const apiConnectionConfigurations: ComputedRef<
      Array<ApiConnectionConfiguration>
    > = computed(
      () => store.getters['engagements/getApiConnectionConfigurations']
    );

    const getApiNameKey = (apiName: ApiName | undefined): string => {
      if (apiName === undefined) return '';
      return (
        Object.keys(ApiName).find(
          key => ApiName[key as keyof typeof ApiName] === apiName
        ) || ''
      );
    };

    const isApiConnectionDisabled = (apiName: ApiName | undefined): boolean => {
      if (apiName === undefined) return true;
      const config = apiConnectionConfigurations.value.find(
        c => c.apiName === apiName
      );
      return config ? !config.enabled : true;
    };

    const runnableApiNextRuns = computed(() => {
      return apiNextRuns.value.filter(
        run =>
          !isApiConnectionDisabled(run.apiName) &&
          run.nextRunCalculationResult === 0 &&
          run.nextRunTimestamp != null &&
          run.apiName !== undefined &&
          run.outputFileName !== undefined
      );
    });

    const canRunImport = (row: ApiNextRunConfiguration): boolean => {
      return (
        !isApiConnectionDisabled(row.apiName) &&
        row.nextRunCalculationResult === NextRunCalculationResult.Success &&
        row.nextRunTimestamp != null
      );
    };

    const getStatusInfo = (
      resultType: number | undefined
    ): { icon: any; colorClass: string; text: string } => {
      if (resultType === NextRunCalculationResult.Success) {
        return {
          icon: CircleCheckFilled,
          colorClass: 'color-success',
          text: i18n.global.t('Success')
        };
      } else if (resultType === NextRunCalculationResult.EndOfSchedule) {
        return {
          icon: InfoFilled,
          colorClass: 'color-info',
          text: i18n.global.t('endOfSchedule')
        };
      } else {
        return {
          icon: CircleCloseFilled,
          colorClass: 'color-error',
          text: i18n.global.t('Failed')
        };
      }
    };

    const formatTimestamp = (input: string | null | undefined) => {
      if (!input) return '';
      return moment(input).format('YYYY-MM-DD HH:mm');
    };

    const buildStartApiRunRequest = (
      imports: RunApiImport[]
    ): StartApiRunRequest => {
      const groupedByApiName = imports.reduce((groupedImports, importItem) => {
        if (!groupedImports[importItem.apiName]) {
          groupedImports[importItem.apiName] = [];
        }
        groupedImports[importItem.apiName].push(importItem.outputFileName);
        return groupedImports;
      }, {} as Record<ApiName, string[]>);

      const apiRunSets: StartApiRunSet[] = Object.keys(groupedByApiName).map(
        apiNameKey => ({
          apiName: Number(apiNameKey) as ApiName,
          outputFileNames: groupedByApiName[apiNameKey]
        })
      );

      return {
        apiRunSets
      };
    };

    const runApiImports = async (imports: RunApiImport[]) => {
      ElMessage({
        showClose: true,
        message: i18n.global.t('runningApiImports')
      });
      const request = buildStartApiRunRequest(imports);
      await store.dispatch('gain/startApiRunImports', request);
    };

    const handleRunMultipleConfirm = (selection: ApiImportSelection) => {
      runApiImports(selection.selectedImports);
      showRunMultipleModal.value = false;
    };

    const navigateToConfiguration = () => {
      router.push({ name: 'GainScheduledApiImportFiles' });
    };

    const updateLocalVars = async () => {
      const engagementId = store.state.home.currentEngagement.id;
      if (!engagementId) return;
      await Promise.all([
        store.dispatch('gain/fetchApiNextRuns'),
        store.dispatch(
          'engagements/fetchApiConnectionConfigurations',
          engagementId
        )
      ]);
    };

    onMounted(async () => {
      showLoadingTable.value = true;
      await updateLocalVars();
      showLoadingTable.value = false;
    });

    return {
      apiConnectionConfigurations,
      apiNextRuns,
      canRunImport,
      formatTimestamp,
      getApiNameKey,
      getStatusInfo,
      handleRunMultipleConfirm,
      isApiConnectionDisabled,
      navigateToConfiguration,
      runnableApiNextRuns,
      runApiImports,
      showLoadingTable,
      showRunMultipleModal,
      spacingWidthHeaderComponents
    };
  }
};
</script>
<style scoped lang="scss">
.color-red {
  color: $etp-warning-red;
}

.color-success {
  color: $etp-light-green;
}

.color-error {
  color: $etp-warning-red;
}

.color-info {
  color: $etp-deloitte-blue-7;
}

.status-with-tooltip {
  cursor: pointer;
}

.button-row {
  display: flex;
  width: 100%;
  gap: 8px;
  justify-content: flex-end;
}

.configure-button {
  h4 {
    transition: border-bottom 0.3s ease;
    border-bottom: 2px solid transparent;
  }

  &:hover h4 {
    border-bottom: 2px solid currentColor;
  }
}
</style>
